/**
 * "Companies Management" — modale master per creare/modificare una ragione
 * sociale dentro un tenant. Parità campi e pattern UX col modale tenant
 * `CompanyFormModal` (sezioni collassabili FormSection/FormField con icona
 * colorata), SENZA credenziali e azioni A-Cube (write-only tenant-side,
 * Task #2808) e senza autocomplete indirizzo. In create sono obbligatorie le
 * info base: indirizzo completo + tipo soggetto + regime fiscale (schema
 * backend MasterCompanyCreateSchema).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Loader2, MapPin, Phone as PhoneIcon, Mail, Hash, Server, Building2, Tag, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { SearchableSelect } from '@easyflow/ui-master/components/ui/searchable-select';
import {
  useLegalForms,
  FormSection as Section,
  FormField as Field,
  TAX_REGIMES,
  PROVINCE_REA,
} from '@easyflow/ui-frontend';
import { useToast } from '@/hooks/use-toast';
import apiClient, { ApiRequestError } from '@/lib/api';
import { toErr } from '@easyflow/shared';
import type { CompanyInput } from '@easyflow/shared';

type SubjectType = 'company' | 'sole_proprietorship' | 'freelancer';

/** Codici insegna — lato master i codici sono SEMPRE editabili (GUC backend). */
interface BrandOption { id: string; code: string; display_name: string; is_active: boolean }

interface CompanyBrandEntry {
  brand_id: string;
  company_code: string;
  brand_display_name: string;
  brand_code: string;
}

const COMPANY_CODE_RE = /^[A-Za-z0-9._\-]{1,64}$/;

function serializeBrands(entries: CompanyBrandEntry[]): string {
  return entries.map((e) => `${e.brand_id}:${e.company_code.trim()}`).sort().join('|');
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

/** Dettaglio RS dal GET /master/companies/:id (shape mapCompanyRow, subset usato dal form). */
export interface MasterCompanyDetail {
  id: string;
  tenant_id: string;
  legal_name: string;
  subject_type?: SubjectType;
  vat_number?: string | null;
  tax_code?: string | null;
  tax_regime?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  legal_form?: string | null;
  rea_office?: string | null;
  rea_number?: string | null;
  share_capital?: number | string | null;
  share_capital_paid_up?: number | string | null;
  socio_unico?: 'SU' | 'SM' | null;
  stato_liquidazione?: 'LN' | 'LS' | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  pec_email?: string | null;
  sdi_code?: string | null;
  fattura_pa_destinazione?: string | null;
  representative_fiscal_code?: string | null;
  fiscal_provider?: 'none' | 'acube' | 'epson' | 'rch' | 'custom' | null;
}

interface CompanyFormModalMasterProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Se valorizzata → edit; altrimenti create. */
  company?: MasterCompanyDetail | null;
}

interface FormState {
  tenant_id: string;
  legal_name: string;
  subject_type: SubjectType;
  vat_number: string;
  tax_code: string;
  tax_regime: string;
  first_name: string;
  last_name: string;
  legal_form: string;
  rea_office: string;
  rea_number: string;
  share_capital: string;
  share_capital_paid_up: string;
  socio_unico: '' | 'SU' | 'SM';
  stato_liquidazione: '' | 'LN' | 'LS';
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone: string;
  email: string;
  pec_email: string;
  sdi_code: string;
  fattura_pa_destinazione: string;
  representative_fiscal_code: string;
  fiscal_provider: 'none' | 'acube';
}

const emptyForm = (): FormState => ({
  tenant_id: '',
  legal_name: '',
  subject_type: 'company',
  vat_number: '',
  tax_code: '',
  tax_regime: '',
  first_name: '',
  last_name: '',
  legal_form: '',
  rea_office: '',
  rea_number: '',
  share_capital: '',
  share_capital_paid_up: '',
  socio_unico: '',
  stato_liquidazione: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'Italia',
  phone: '',
  email: '',
  pec_email: '',
  sdi_code: '',
  fattura_pa_destinazione: '',
  representative_fiscal_code: '',
  fiscal_provider: 'none',
});

const nz = (v: string | number | null | undefined): string => (v === null || v === undefined ? '' : String(v));

function fromDetail(c: MasterCompanyDetail): FormState {
  return {
    tenant_id: c.tenant_id,
    legal_name: c.legal_name ?? '',
    subject_type: c.subject_type ?? 'company',
    vat_number: nz(c.vat_number),
    tax_code: nz(c.tax_code),
    tax_regime: nz(c.tax_regime),
    first_name: nz(c.first_name),
    last_name: nz(c.last_name),
    legal_form: nz(c.legal_form),
    rea_office: nz(c.rea_office),
    rea_number: nz(c.rea_number),
    share_capital: nz(c.share_capital),
    share_capital_paid_up: nz(c.share_capital_paid_up),
    socio_unico: c.socio_unico ?? '',
    stato_liquidazione: c.stato_liquidazione ?? '',
    address: nz(c.address),
    city: nz(c.city),
    state: nz(c.state),
    postal_code: nz(c.postal_code),
    country: nz(c.country) || 'Italia',
    phone: nz(c.phone),
    email: nz(c.email),
    pec_email: nz(c.pec_email),
    sdi_code: nz(c.sdi_code),
    fattura_pa_destinazione: nz(c.fattura_pa_destinazione),
    representative_fiscal_code: nz(c.representative_fiscal_code),
    fiscal_provider: c.fiscal_provider === 'acube' ? 'acube' : 'none',
  };
}

interface FieldErrorItem {
  field: string;
  message: string;
}

function extractFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiRequestError)) return {};
  const body = err.body as { errors?: FieldErrorItem[]; field?: string; error?: string } | null;
  const map: Record<string, string> = {};
  if (Array.isArray(body?.errors)) {
    for (const e of body.errors) {
      if (e.field) map[e.field] = e.message;
    }
  } else if (body?.field) {
    map[body.field] = typeof body.error === 'string' ? body.error : 'Valore non valido';
  }
  return map;
}

const nativeSelectClass = 'h-9 w-full border rounded-md px-2 text-sm bg-background';

export function CompanyFormModalMaster({ isOpen, onClose, onSuccess, company }: CompanyFormModalMasterProps) {
  const { toast } = useToast();
  const isEdit = Boolean(company?.id);
  const { legalForms } = useLegalForms();

  const [isSaving, setIsSaving] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showRepresentative, setShowRepresentative] = useState(false);
  // Codici insegna (sezione "Insegne") — master: codici sempre editabili.
  const [brandEntries, setBrandEntries] = useState<CompanyBrandEntry[]>([]);
  const [brandCatalog, setBrandCatalog] = useState<BrandOption[]>([]);
  const [brandSnapshot, setBrandSnapshot] = useState('');
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [brandsError, setBrandsError] = useState<string | null>(null);

  const loadTenants = useCallback(async () => {
    try {
      const res = await apiClient.get<TenantOption[]>('/master/companies/tenants');
      setTenants(res.data ?? []);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    if (company) {
      setForm(fromDetail(company));
      setShowRepresentative(Boolean(company.representative_fiscal_code));
    } else {
      setForm(emptyForm());
      setShowRepresentative(false);
      void loadTenants();
    }
    // Insegne: catalogo globale + associazioni esistenti in edit.
    setBrandEntries([]);
    setBrandSnapshot(serializeBrands([]));
    setBrandPickerOpen(false);
    setBrandsError(null);
    void (async () => {
      try {
        const catalogRes = await apiClient.get<{ brands: BrandOption[] }>('/master/retail-brand-channels');
        setBrandCatalog((catalogRes.data?.brands ?? []).filter((b) => b.is_active));
        if (company?.id) {
          const res = await apiClient.get<Array<{
            brand_id: string; company_code: string;
            brand_display_name: string; brand_code: string;
          }>>(`/master/companies/${company.id}/brands`);
          const entries: CompanyBrandEntry[] = (res.data ?? []).map((r) => ({
            brand_id: r.brand_id,
            company_code: r.company_code,
            brand_display_name: r.brand_display_name,
            brand_code: r.brand_code,
          }));
          setBrandEntries(entries);
          setBrandSnapshot(serializeBrands(entries));
        }
      } catch {
        setBrandsError('Caricamento insegne non riuscito');
      }
    })();
  }, [isOpen, company, loadTenants]);

  const addBrandEntry = (brandId: string): void => {
    const opt = brandCatalog.find((b) => b.id === brandId);
    if (!opt) return;
    setBrandEntries((prev) => prev.some((e) => e.brand_id === brandId)
      ? prev
      : [...prev, { brand_id: brandId, company_code: '', brand_display_name: opt.display_name, brand_code: opt.code }]);
    setBrandPickerOpen(false);
  };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const isCompany = form.subject_type === 'company';
  const isIndividual = form.subject_type === 'sole_proprietorship' || form.subject_type === 'freelancer';

  const payload = useMemo((): CompanyInput & { tenant_id?: string } => {
    const base: CompanyInput = {
      legal_name: form.legal_name.trim(),
      subject_type: form.subject_type,
      vat_number: form.vat_number.trim() || undefined,
      tax_code: form.tax_code.trim() || undefined,
      tax_regime: form.tax_regime || undefined,
      first_name: isIndividual ? (form.first_name.trim() || undefined) : undefined,
      last_name: isIndividual ? (form.last_name.trim() || undefined) : undefined,
      legal_form: isCompany ? (form.legal_form || undefined) : undefined,
      rea_office: isCompany ? (form.rea_office || undefined) : undefined,
      rea_number: isCompany ? (form.rea_number.trim() || undefined) : undefined,
      share_capital: isCompany && form.share_capital !== '' ? Number(form.share_capital) : undefined,
      share_capital_paid_up: isCompany && form.share_capital_paid_up !== '' ? Number(form.share_capital_paid_up) : undefined,
      socio_unico: isCompany && form.socio_unico !== '' ? form.socio_unico : undefined,
      stato_liquidazione: isCompany && form.stato_liquidazione !== '' ? form.stato_liquidazione : undefined,
      address: form.address.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      postal_code: form.postal_code.trim() || undefined,
      country: form.country.trim() || 'Italy',
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      pec_email: form.pec_email.trim() || undefined,
      sdi_code: form.sdi_code.trim() || undefined,
      fattura_pa_destinazione: form.fattura_pa_destinazione.trim() || undefined,
      representative_fiscal_code: showRepresentative ? (form.representative_fiscal_code.trim() || undefined) : '',
      fiscal_provider: form.fiscal_provider,
    };
    return base;
  }, [form, isCompany, isIndividual, showRepresentative]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFieldErrors({});

    if (!isEdit && !form.tenant_id) {
      toast({ title: 'Tenant obbligatorio', description: 'Seleziona il tenant di destinazione.', variant: 'destructive' });
      return;
    }
    if (!form.legal_name.trim()) {
      toast({ title: 'Ragione sociale obbligatoria', description: 'Inserisci la denominazione.', variant: 'destructive' });
      return;
    }
    if (!isEdit) {
      // Info base obbligatorie in create (specchio schema backend).
      const missing: string[] = [];
      if (!form.address.trim()) missing.push('indirizzo');
      if (!form.city.trim()) missing.push('città');
      if (!form.postal_code.trim()) missing.push('CAP');
      if (!form.tax_regime) missing.push('regime fiscale');
      if (missing.length > 0) {
        toast({ title: 'Campi obbligatori mancanti', description: `Compila: ${missing.join(', ')}.`, variant: 'destructive' });
        return;
      }
    }

    // Insegne: codice obbligatorio e valido per ogni associazione.
    const badBrand = brandEntries.find((e) => !COMPANY_CODE_RE.test(e.company_code.trim()));
    if (badBrand) {
      setBrandsError(`Codice mancante o non valido per l'insegna ${badBrand.brand_display_name} (1-64 caratteri: lettere, numeri, punto, underscore, trattino).`);
      return;
    }
    setBrandsError(null);
    const brandsChanged = serializeBrands(brandEntries) !== brandSnapshot;
    const brandItems = brandEntries.map((e) => ({ brand_id: e.brand_id, company_code: e.company_code.trim() }));

    setIsSaving(true);
    try {
      if (isEdit && company) {
        await apiClient.put(`/master/companies/${company.id}`, payload);
        if (brandsChanged) {
          await apiClient.put(`/master/companies/${company.id}/brands`, { brands: brandItems });
        }
        toast({ title: 'Ragione sociale aggiornata', description: 'Le modifiche sono state salvate.' });
      } else {
        const created = await apiClient.post<{ id: string }>('/master/companies', { ...payload, tenant_id: form.tenant_id });
        const newId = created.data?.id;
        if (brandsChanged && newId) {
          try {
            await apiClient.put(`/master/companies/${newId}/brands`, { brands: brandItems });
          } catch (brandErr) {
            toast({
              title: 'Insegne non salvate',
              description: `${toErr(brandErr).message} Riapri la ragione sociale in modifica per riprovare.`,
              variant: 'destructive',
            });
          }
        }
        toast({ title: 'Ragione sociale creata', description: 'Creata nel tenant selezionato.' });
      }
      onSuccess();
      onClose();
    } catch (err) {
      const errs = extractFieldErrors(err);
      if (Object.keys(errs).length > 0) setFieldErrors(errs);
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSaving) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            {isEdit ? `Modifica RS — ${company?.legal_name ?? ''}` : 'Nuova ragione sociale tenant'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Dati anagrafici e fiscali della ragione sociale. Credenziali e onboarding fiscale restano gestiti dal tenant.
          </p>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3 mt-2">
          {!isEdit && (
            <Section title="Tenant di destinazione" icon={<Building2 className="h-3.5 w-3.5" />} iconClassName="bg-sky-500/10 text-sky-600 dark:text-sky-400">
              <Field label="Tenant" required>
                <SearchableSelect
                  value={form.tenant_id}
                  onValueChange={(v) => set('tenant_id', v)}
                  options={tenants.map((t) => ({ value: t.id, label: `${t.name} (${t.slug})` }))}
                  placeholder="Seleziona tenant…"
                  searchPlaceholder="Cerca tenant…"
                  emptyText="Nessun tenant trovato"
                />
              </Field>
            </Section>
          )}

          <Section title="Identificativi" icon={<Hash className="h-3.5 w-3.5" />} iconClassName="bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <Field label="Ragione sociale" required error={fieldErrors.legal_name}>
              <Input value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} placeholder="Nome azienda S.r.l." className="h-9" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <Field label="Tipo soggetto" required>
                <select
                  className={nativeSelectClass}
                  value={form.subject_type}
                  onChange={(e) => set('subject_type', e.target.value as SubjectType)}
                >
                  <option value="company">Società</option>
                  <option value="sole_proprietorship">Ditta individuale</option>
                  <option value="freelancer">Libero professionista</option>
                </select>
              </Field>
              <Field label="Partita IVA" error={fieldErrors.vat_number}>
                <Input value={form.vat_number} onChange={(e) => set('vat_number', e.target.value)} placeholder="IT12345678901" className="h-9" />
              </Field>
              <Field label="Codice Fiscale" error={fieldErrors.tax_code}>
                <Input value={form.tax_code} onChange={(e) => set('tax_code', e.target.value)} placeholder="12345678901" className="h-9" />
              </Field>
              <Field label="Regime fiscale" required={!isEdit} error={fieldErrors.tax_regime}>
                <select
                  className={nativeSelectClass}
                  value={form.tax_regime}
                  onChange={(e) => set('tax_regime', e.target.value)}
                >
                  <option value="">— Seleziona —</option>
                  {TAX_REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
              {isIndividual && (
                <>
                  <Field label="Nome" error={fieldErrors.first_name}>
                    <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="h-9" />
                  </Field>
                  <Field label="Cognome" error={fieldErrors.last_name}>
                    <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="h-9" />
                  </Field>
                </>
              )}
            </div>
            {isCompany && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 pt-2 border-t border-border">
                <Field label="Forma giuridica" error={fieldErrors.legal_form}>
                  <select
                    className={nativeSelectClass}
                    value={form.legal_form}
                    onChange={(e) => set('legal_form', e.target.value)}
                  >
                    <option value="">— Seleziona —</option>
                    {legalForms.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
                  </select>
                </Field>
                <Field label="REA — Provincia" error={fieldErrors.rea_office}>
                  <select
                    className={nativeSelectClass}
                    value={form.rea_office}
                    onChange={(e) => set('rea_office', e.target.value)}
                  >
                    <option value="">—</option>
                    {PROVINCE_REA.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="REA — Numero" error={fieldErrors.rea_number}>
                  <Input value={form.rea_number} onChange={(e) => set('rea_number', e.target.value.toUpperCase())} placeholder="MI-1234567" className="h-9" />
                </Field>
                <Field label="Capitale sociale (€)" error={fieldErrors.share_capital}>
                  <Input type="number" step="0.01" min="0" value={form.share_capital} onChange={(e) => set('share_capital', e.target.value)} placeholder="10000.00" className="h-9" />
                </Field>
                <Field label="Capitale versato (€)">
                  <Input type="number" step="0.01" min="0" value={form.share_capital_paid_up} onChange={(e) => set('share_capital_paid_up', e.target.value)} placeholder="10000.00" className="h-9" />
                </Field>
                <Field label="Socio unico" error={fieldErrors.socio_unico}>
                  <select
                    className={nativeSelectClass}
                    value={form.socio_unico}
                    onChange={(e) => set('socio_unico', e.target.value as FormState['socio_unico'])}
                  >
                    <option value="">— Seleziona —</option>
                    <option value="SU">SU — Socio unico</option>
                    <option value="SM">SM — Più soci</option>
                  </select>
                </Field>
                <Field label="Stato liquidazione" error={fieldErrors.stato_liquidazione}>
                  <select
                    className={nativeSelectClass}
                    value={form.stato_liquidazione}
                    onChange={(e) => set('stato_liquidazione', e.target.value as FormState['stato_liquidazione'])}
                  >
                    <option value="">— Seleziona —</option>
                    <option value="LN">LN — Non in liquidazione</option>
                    <option value="LS">LS — In liquidazione</option>
                  </select>
                </Field>
              </div>
            )}
          </Section>

          <Section title="Insegne" icon={<Tag className="h-3.5 w-3.5" />} iconClassName="bg-cyan-500/10 text-cyan-600 dark:text-cyan-400" defaultOpen={false}>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Associa le insegne alla ragione sociale e assegna il codice identificativo.
                Lato master il codice è sempre modificabile (immutabile per il tenant).
              </p>
              {brandEntries.map((e) => (
                <div key={e.brand_id} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{e.brand_display_name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{e.brand_code}</span>
                  </div>
                  <div className="flex flex-col gap-1 flex-1 max-w-xs">
                    <Input
                      value={e.company_code}
                      onChange={(ev) => setBrandEntries((prev) => prev.map((x) => x.brand_id === e.brand_id ? { ...x, company_code: ev.target.value } : x))}
                      placeholder="Codice RS * (es: RS-BO-01)"
                      className="h-9"
                    />
                    {e.company_code.trim() === '' && (
                      <span className="text-[11px] text-destructive">Codice obbligatorio</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBrandEntries((prev) => prev.filter((x) => x.brand_id !== e.brand_id))}
                    title="Rimuovi insegna"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {brandPickerOpen ? (
                <Select onValueChange={addBrandEntry}>
                  <SelectTrigger className="h-9 w-full max-w-xs">
                    <SelectValue placeholder="Seleziona insegna…" />
                  </SelectTrigger>
                  <SelectContent>
                    {brandCatalog.filter((b) => !brandEntries.some((e) => e.brand_id === b.id)).map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.display_name} ({b.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => setBrandPickerOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Associa insegna
                </Button>
              )}
              {brandsError && <p className="text-xs text-destructive">{brandsError}</p>}
            </div>
          </Section>

          <Section title="Sede legale" icon={<MapPin className="h-3.5 w-3.5" />} iconClassName="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" defaultOpen={!isEdit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Field label="Indirizzo" required={!isEdit} error={fieldErrors.address}>
                  <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Via Roma 1" className="h-9" />
                </Field>
              </div>
              <Field label="Città" required={!isEdit} error={fieldErrors.city}>
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Milano" className="h-9" />
              </Field>
              <Field label="Provincia">
                <Input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="MI" maxLength={2} className="h-9" />
              </Field>
              <Field label="CAP" required={!isEdit} error={fieldErrors.postal_code}>
                <Input value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} placeholder="20100" maxLength={5} className="h-9" />
              </Field>
              <Field label="Paese">
                <Input value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="Italia" className="h-9" />
              </Field>
            </div>
          </Section>

          <Section title="Contatti" icon={<PhoneIcon className="h-3.5 w-3.5" />} iconClassName="bg-amber-500/10 text-amber-600 dark:text-amber-400" defaultOpen={false}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Telefono">
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+39 02 12345678" className="pl-9 h-9" />
                </div>
              </Field>
              <Field label="Email">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="info@azienda.it" className="pl-9 h-9" />
                </div>
              </Field>
            </div>
          </Section>

          <Section title="Fatturazione elettronica e PEC" icon={<FileText className="h-3.5 w-3.5" />} iconClassName="bg-violet-500/10 text-violet-600 dark:text-violet-400" defaultOpen={false}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="PEC" error={fieldErrors.pec_email}>
                <Input type="email" value={form.pec_email} onChange={(e) => set('pec_email', e.target.value)} placeholder="azienda@pec.it" className="h-9" />
              </Field>
              <Field label="Codice SDI">
                <Input value={form.sdi_code} onChange={(e) => set('sdi_code', e.target.value)} placeholder="ABC1234" maxLength={7} className="h-9" />
              </Field>
              <Field label="Cod. destinatario PA">
                <Input
                  value={form.fattura_pa_destinazione}
                  onChange={(e) => set('fattura_pa_destinazione', e.target.value.toUpperCase())}
                  placeholder="UFQ7N8 (codice IPA)"
                  maxLength={7}
                  className="h-9"
                  title="6 caratteri per PA (IPA), 7 per B2B (SDI)"
                />
              </Field>
            </div>
            <div className="pt-3 border-t border-border space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showRepresentative}
                  onChange={(e) => setShowRepresentative(e.target.checked)}
                  className="h-4 w-4"
                />
                Rappresentante fiscale diverso dal titolare
              </label>
              {showRepresentative && (
                <Field label="CF rappresentante fiscale" error={fieldErrors.representative_fiscal_code}>
                  <Input
                    value={form.representative_fiscal_code}
                    onChange={(e) => set('representative_fiscal_code', e.target.value.toUpperCase())}
                    placeholder="RSSMRA80A01H501Z"
                    maxLength={16}
                    className="h-9"
                  />
                </Field>
              )}
            </div>
          </Section>

          <Section title="Provider fiscale" icon={<Server className="h-3.5 w-3.5" />} iconClassName="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" defaultOpen={false}>
            <Field label="Provider per fatturazione/scontrini">
              <select
                className={nativeSelectClass}
                value={form.fiscal_provider}
                onChange={(e) => set('fiscal_provider', e.target.value as FormState['fiscal_provider'])}
              >
                <option value="none">Nessuno</option>
                <option value="acube">A-Cube</option>
              </select>
            </Field>
            {form.fiscal_provider === 'acube' && (
              <p className="text-xs text-muted-foreground">
                Identità, credenziali e onboarding A-Cube si configurano dal tenant (pagina Ragioni sociali).
              </p>
            )}
          </Section>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>Annulla</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Salvataggio…</> : isEdit ? 'Salva modifiche' : 'Crea ragione sociale'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
